import { z } from 'zod';

import { Summary, SummaryLike } from '../summary';
import { entityTypeSchema } from './metadata-association';
import { Delta, deltaKindSchema } from './sync';

/**
 * An object ID originating from the external data source Edlink.
 */
export const edlinkIdSchema = z.string().brand<'edlinkId'>();
export const EdlinkId = edlinkIdSchema.parse.bind(edlinkIdSchema);
export type EdlinkId = z.infer<typeof edlinkIdSchema>;

export const edlinkDistrictEntrySchema = z.object({
  integrationId: edlinkIdSchema,
  districtId: edlinkIdSchema,
  districtName: z.string(),
});
export type EdlinkDistrictEntry = z.infer<typeof edlinkDistrictEntrySchema>;

export const edlinkOrgChartImportSuccessSchema = z.object({
  type: entityTypeSchema,
  kind: deltaKindSchema,
  internalId: z.string().optional(),
  externalId: edlinkIdSchema.optional(),
  details: z.record(z.string(), z.string()).optional(),
});
export type EdlinkOrgChartImportSuccess = z.infer<typeof edlinkOrgChartImportSuccessSchema>;

export const edlinkOrgChartImportFailureSchema = z.object({
  type: entityTypeSchema,
  kind: deltaKindSchema,
  internalId: z.string().optional(),
  externalId: edlinkIdSchema.optional(),
  error: z.instanceof(Error),
});
export type EdlinkOrgChartImportFailure = z.infer<typeof edlinkOrgChartImportFailureSchema>;

export const edlinkOrgChartImportSkipSchema = z.object({
  type: entityTypeSchema,
  kind: deltaKindSchema,
  internalId: z.string().optional(),
  externalId: edlinkIdSchema.optional(),
  reason: z.string(),
});
export type EdlinkOrgChartImportSkip = z.infer<typeof edlinkOrgChartImportSkipSchema>;

export class EdlinkOrgChartImportSummary extends Summary<
  EdlinkOrgChartImportSuccess,
  EdlinkOrgChartImportFailure,
  EdlinkOrgChartImportSkip
> {
  get items() {
    return this.results.map(({ type, kind, internalId, externalId, details }) => ({
      type,
      kind,
      internalId,
      externalId,
      label: labelForEdlinkResult(type, kind, internalId, externalId),
      details,
    }));
  }

  get failureItems() {
    return this.failures.map(({ type, kind, internalId, externalId, error }) => ({
      type,
      kind,
      internalId,
      externalId,
      label: labelForEdlinkResult(type, kind, internalId, externalId, 'failed'),
      details: { reason: (error as any).userFacingMessage ?? error.message },
    }));
  }

  get skipItems() {
    return this.skips.map(({ type, kind, internalId, externalId, reason }) => ({
      type,
      kind,
      internalId,
      externalId,
      label: labelForEdlinkResult(type, kind, internalId, externalId, 'skipped'),
      details: { reason },
    }));
  }
}
export type EdlinkOrgChartImportSummaryLike = SummaryLike<
  EdlinkOrgChartImportSuccess,
  EdlinkOrgChartImportFailure,
  EdlinkOrgChartImportSkip
>;

export const edlinkOrgChartDryRunItemSchema = z.object({
  delta: entityTypeSchema,
  kind: deltaKindSchema,
  label: z.string().optional(),
  details: z.record(z.string(), z.string()).optional(),
});
export type EdlinkOrgChartDryRunItem = z.infer<typeof edlinkOrgChartDryRunItemSchema>;
export type EdlinkOrgChartDryRunResult = {
  items: Delta<string, object>[];
  failures: EdlinkOrgChartDryRunItem[];
  skips: EdlinkOrgChartDryRunItem[];
};

export function labelForEdlinkResult(
  type: string,
  kind: string,
  internalId: string | undefined,
  externalId: string | undefined,
  state: 'success' | 'failed' | 'skipped' = 'success',
): string {
  const entity = type.charAt(0).toUpperCase() + type.slice(1);
  const id = internalId ?? externalId;

  const [verbTo, verbIng, action] =
    kind === 'create'
      ? ['create', 'creating', 'Created']
      : kind === 'update'
        ? ['update', 'updating', 'Updated']
        : ['delete', 'deleting', 'Deleted'];

  const prefix =
    state === 'failed'
      ? `Failed to ${verbTo} ${entity}`
      : state === 'skipped'
        ? `Skipped ${verbIng} ${entity}`
        : `${action} ${entity}`;

  const suffix = kind === 'create' ? `with external ID ${externalId}` : `with ID ${internalId}`;

  return id ? `${prefix} ${suffix}` : prefix;
}
